/** GPU Pixelate — two-pass downsample + nearest-neighbor upsample.
 *
 *  Fragment-shader port of `lib/filters/pixelate.ts`. The CPU path
 *  walks the source in N×N blocks, computes each block's mean
 *  RGBA, and fills the block with that mean. The GPU equivalent
 *  factors that into two passes:
 *
 *    Pass 0 (downsample): renders to a small offscreen canvas of
 *      size (ceil(W/N), ceil(H/N)). Each output pixel is the mean
 *      of its corresponding N×N source block — read via
 *      `texelFetch` so the byte-parity sum walks the same source
 *      pixels the CPU does.
 *    Pass 1 (upsample): renders to the full-size target. Each
 *      output pixel is the downsampled texel for its containing
 *      block, sampled with `texelFetch` against the small canvas.
 *      Same nearest-neighbor "fill the block" semantics as CPU.
 *
 *  This requires the runtime's `targetDims` extension — pass 0's
 *  output is smaller than its input, pass 1's output is larger.
 *  Without it, we'd be stuck with full-size FBOs throughout and
 *  the downsample pass would do massively redundant work (40k
 *  samples per output pixel at block=200).
 *
 *  Edge handling: source dimensions that aren't a multiple of
 *  blockSize produce a partial last block in each row/column. The
 *  CPU averages only the in-bounds pixels of that partial block
 *  (`Math.min(h, by + bs)`); we mirror that via runtime cutoff in
 *  the shader's accumulation loop.
 *
 *  Identity short-circuit at blockSize === 1. Programmatic callers
 *  exceeding MAX_BLOCK_SIZE fall back to CPU.
 *
 *  ── Y-axis ─────────────────────────────────────────────────────
 *  Both shaders compute integer pixel coords from v_uv in
 *  CPU-aligned top-down convention via `(1.0 - v_uv.y)`. Texel
 *  reads use texelFetch with the FLIP_Y compensation
 *  (`srcHi - sy`) so the per-block source set matches the CPU's
 *  `Math.min(maxY, ...)` window. */

import type { PixelateValue } from '../../filters/pixelate';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const MAX_BLOCK_SIZE = 200;

const DOWNSAMPLE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_srcDims;
uniform vec2 u_outDims;
uniform int u_blockSize;
out vec4 outColor;

void main() {
  // Output block index in CPU top-down convention. v_uv.y is bottom-
  // up in the runtime; the (1.0 - v_uv.y) flip aligns with CPU's y.
  int blockX = int(floor(v_uv.x * u_outDims.x));
  int blockY = int(floor((1.0 - v_uv.y) * u_outDims.y));

  int sxStart = blockX * u_blockSize;
  int syStart = blockY * u_blockSize;
  int srcW = int(u_srcDims.x);
  int srcH = int(u_srcDims.y);
  int sxEnd = min(sxStart + u_blockSize, srcW);
  int syEnd = min(syStart + u_blockSize, srcH);
  int srcHi = srcH - 1;

  vec4 sum = vec4(0.0);
  int count = 0;

  // Constant-bound loop with runtime cutoff. The loop is unrolled
  // up to MAX_BLOCK_SIZE per axis, but per-block work is gated to
  // the actual blockSize via the early break.
  for (int dy = 0; dy < ${MAX_BLOCK_SIZE}; dy++) {
    int sy = syStart + dy;
    if (sy >= syEnd) break;
    for (int dx = 0; dx < ${MAX_BLOCK_SIZE}; dx++) {
      int sx = sxStart + dx;
      if (sx >= sxEnd) break;
      // The source texture was uploaded with UNPACK_FLIP_Y, so the
      // CPU's top-down row sy is at texture row srcHi - sy.
      sum += texelFetch(u_source, ivec2(sx, srcHi - sy), 0);
      count++;
    }
  }

  outColor = sum / float(count);
}
`;

const UPSAMPLE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;       // downsampled, sized (numBlocksX, numBlocksY)
uniform vec2 u_dims;              // full output (W, H)
uniform vec2 u_downDims;          // (numBlocksX, numBlocksY)
uniform int u_blockSize;
out vec4 outColor;

void main() {
  // Output pixel position in CPU top-down convention.
  int xDom = int(floor(v_uv.x * u_dims.x));
  int yDom = int(floor((1.0 - v_uv.y) * u_dims.y));
  // Integer-division block index — same arithmetic the CPU's outer
  // loop uses (by += bs / bx += bs).
  int blockX = xDom / u_blockSize;
  int blockY = yDom / u_blockSize;
  int downHi = int(u_downDims.y) - 1;
  // texelFetch with FLIP_Y compensation: the downsampled texture
  // was uploaded from a canvas2D blit of pass 0's GL canvas (DOM-
  // oriented), so the same srcHi - sy convention applies.
  outColor = texelFetch(u_source, ivec2(blockX, downHi - blockY), 0);
}
`;

export function applyPixelateOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: PixelateValue,
): boolean {
  // Same clamp the CPU path uses (1..200). Integer for the same
  // block-boundary arithmetic.
  const blockSize = Math.max(1, Math.min(MAX_BLOCK_SIZE, Math.floor(value.blockSize)));
  if (blockSize === 1) return applyIdentityCopy(source, target);

  const w = source.width;
  const h = source.height;
  const numBlocksX = Math.ceil(w / blockSize);
  const numBlocksY = Math.ceil(h / blockSize);

  // Intermediate canvas holding the downsampled block-means. Allocated
  // fresh each call — small enough (worst-case at blockSize=2 on 4K:
  // 2000×2000 = ~16MB) that pooling isn't worth the complexity. The
  // `willReadFrequently: true` hint locks the canvas into CPU-readable
  // mode but doesn't block GPU writes via drawImage from the runtime.
  const downCanvas = document.createElement('canvas');
  downCanvas.width = numBlocksX;
  downCanvas.height = numBlocksY;

  // Pass 0: full source -> downsampled.
  const pass0Ok = applyShader({
    source,
    target: downCanvas,
    targetDims: { width: numBlocksX, height: numBlocksY },
    fragShaderSrc: DOWNSAMPLE_FRAG,
    uniforms: {
      u_srcDims: { type: 'vec2', value: [w, h] },
      u_outDims: { type: 'vec2', value: [numBlocksX, numBlocksY] },
      u_blockSize: { type: 'int', value: blockSize },
    },
  });
  if (!pass0Ok) return false;

  // Pass 1: downsampled -> full target. The runtime uploads
  // downCanvas as the source texture (small); targetDims forces the
  // output back to full size.
  return applyShader({
    source: downCanvas,
    target,
    targetDims: { width: w, height: h },
    fragShaderSrc: UPSAMPLE_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [w, h] },
      u_downDims: { type: 'vec2', value: [numBlocksX, numBlocksY] },
      u_blockSize: { type: 'int', value: blockSize },
    },
  });
}
